import React, { useState } from "react";
import api from "../../lib/api";
import { X, FileText, Loader2, Wand2, ClipboardPaste } from "lucide-react";

/**
 * Strip HTML and rich-text markup from a string, returning clean plain text.
 * Handles HTML pasted from Word, Google Docs, web pages, etc.
 */
export const stripToPlainText = (input) => {
  if (!input) return "";
  let text = "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${input}</div>`, "text/html");
    // Drop dangerous / non-content elements entirely
    doc.querySelectorAll("script, style, noscript, meta, link, head").forEach((el) => el.remove());
    // Replace block elements with newlines
    doc.querySelectorAll("p, div, br, li, h1, h2, h3, h4, h5, h6").forEach((el) => {
      el.append("\n");
    });
    text = doc.body.textContent || "";
  } catch (_) {
    text = input.replace(/<[^>]*>/g, "");
  }
  // Decode common entities
  const ta = document.createElement("textarea");
  ta.innerHTML = text;
  text = ta.value;
  text = text.replace(/\u00a0/g, " ").replace(/[ \t]+/g, " ").replace(/\n[ \t]+/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return text;
};

/**
 * BlurbImportModal — paste blurb text from any source (auto-strips HTML),
 * or fetch a public Google Docs share URL.
 */
const BlurbImportModal = ({ open, onClose, onPlace }) => {
  const [tab, setTab] = useState("paste");
  const [text, setText] = useState("");
  const [gdocUrl, setGdocUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handlePaste = (e) => {
    e.preventDefault();
    const dt = e.clipboardData;
    const html = dt.getData("text/html");
    const plain = dt.getData("text/plain");
    const cleaned = html ? stripToPlainText(html) : (plain || "").trim();
    // Insert at cursor
    const ta = e.currentTarget;
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const next = text.slice(0, start) + cleaned + text.slice(end);
    setText(next);
    // restore cursor
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = start + cleaned.length;
    }, 0);
  };

  const handleGdocImport = async () => {
    if (!gdocUrl.trim()) return;
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/cover/import-gdocs", { url: gdocUrl.trim() });
      const cleaned = stripToPlainText(data.text || "");
      setText(cleaned);
      setTab("paste");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not fetch Google Doc. Make sure it's public.");
    } finally {
      setBusy(false);
    }
  };

  const handlePlace = () => {
    const cleaned = stripToPlainText(text);
    if (!cleaned) return;
    onPlace(cleaned);
    setText("");
    setGdocUrl("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2C2A]/55 backdrop-blur-sm px-4 py-6 overflow-y-auto" data-testid="blurb-modal">
      <div className="w-full max-w-xl bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl my-auto">
        <div className="px-7 py-5 border-b border-[#D3D1C7] flex items-center justify-between">
          <div>
            <h2 className="text-2xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
              Import blurb text
            </h2>
            <p className="text-sm text-[#5F5E5A] mt-0.5">Paste from anywhere — HTML is automatically stripped to clean text.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" aria-label="Close" data-testid="blurb-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-7 pt-5">
          <div className="flex items-center gap-1 bg-[#F1EFE8] rounded-md p-1 w-fit">
            <button
              onClick={() => setTab("paste")}
              data-testid="blurb-tab-paste"
              className={`px-3 py-1.5 rounded text-xs font-medium ${tab === "paste" ? "bg-white text-[#534AB7] shadow-sm" : "text-[#5F5E5A]"}`}
            >
              <ClipboardPaste className="w-3 h-3 inline mr-1" /> Paste / type
            </button>
            <button
              onClick={() => setTab("gdocs")}
              data-testid="blurb-tab-gdocs"
              className={`px-3 py-1.5 rounded text-xs font-medium ${tab === "gdocs" ? "bg-white text-[#534AB7] shadow-sm" : "text-[#5F5E5A]"}`}
            >
              <FileText className="w-3 h-3 inline mr-1" /> Google Docs
            </button>
          </div>
        </div>

        <div className="px-7 py-5 space-y-4">
          {tab === "paste" && (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              rows={9}
              placeholder="Paste your blurb here from Word, Google Docs, a webpage — formatting will be stripped automatically. You can also type directly."
              data-testid="blurb-paste-area"
              className="w-full px-3 py-2.5 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7] resize-none leading-relaxed"
            />
          )}
          {tab === "gdocs" && (
            <div className="space-y-3">
              <input
                value={gdocUrl}
                onChange={(e) => setGdocUrl(e.target.value)}
                placeholder="https://docs.google.com/document/d/..."
                data-testid="blurb-gdoc-input"
                className="w-full px-3 py-2.5 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7]"
              />
              <p className="text-xs text-[#5F5E5A]">
                Make sure your doc is set to <strong className="text-[#2C2C2A]">"Anyone with the link can view"</strong> before importing.
              </p>
              <button
                onClick={handleGdocImport}
                disabled={!gdocUrl.trim() || busy}
                data-testid="blurb-gdoc-fetch-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {busy ? "Fetching…" : "Fetch & strip HTML"}
              </button>
              {error && <p className="text-sm text-[#BA7517]" data-testid="blurb-error">{error}</p>}
            </div>
          )}
          {text && (
            <div className="text-xs text-[#5F5E5A] flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1D9E75]" />
              {text.length} characters · {text.split(/\s+/).filter(Boolean).length} words
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-[#D3D1C7] flex items-center justify-end gap-3 bg-[#FAFAF9]">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8]"
            data-testid="blurb-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handlePlace}
            disabled={!stripToPlainText(text)}
            data-testid="blurb-place-btn"
            className="px-5 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Wand2 className="w-4 h-4" /> Place on canvas
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlurbImportModal;
